import React, { useState, useEffect, useRef } from "react";
import "./receipt.css";
import { 
  Button, Box, TextField, TableCell, TableRow, InputLabel, Container,
  FormControl, CircularProgress, TableContainer, Dialog, DialogActions, Typography, TableBody, TableHead, Table, Paper,
  DialogContent, Autocomplete, Select, MenuItem
} from "@mui/material";
import api from "../../api/api";
import SweetAlert from "sweetalert2";
import jsBarcode from "jsbarcode";
import html2pdf from "html2pdf.js";
import SchoolLogo from "./logoo.png"; 

function ReceiptPayment() {
  const [students, setStudents] = useState([]);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [payerName, setPayerName] = useState("");
  const [dateName, setDateName] = useState("");
  const [studentClass, setStudentClass] = useState("");
  const [payerContact, setPayerContact] = useState("");
  const [amountDue, setAmountDue] = useState("");
  const [amountPaid, setAmountPaid] = useState("");
  const [semester, setSemester] = useState("Select Semester");
  const [services, setServices] = useState("Select Service");
  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [receiptData, setReceiptData] = useState(null);

  const receiptRef = useRef();

  const ListboxProps = {
    style: {
      maxHeight: 48 * 4.5,
      overflowY: "auto",
    },
  };

  useEffect(() => {
    api.get("/api/users/students/")
      .then((response) => {
        setStudents(response.data);
        if (response.data.length > 0) {
          setSelectedStudent(response.data[0]);
        }
      })
      .catch((error) => {
        console.error("Error fetching students:", error);
        SweetAlert.fire({
          title: "Error!",
          text: "Failed to fetch students data!",
          icon: "error",
        });
      });
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedStudent) {
      SweetAlert.fire({
        title: "Error!",
        text: "Please select a student",
        icon: "error",
      });
      return;
    }
    setLoading(true);

    try {
      const response = await api.post("/api/users/students-reciept/", {
        student: selectedStudent.id,
        student_class: studentClass,
        payer_name: payerName,
        payer_contact: payerContact,
        amount_due: amountDue,
        amount_paid: amountPaid,
        date: dateName,
        semester,
        services,
      });

      setReceiptData(response.data);
      setDialogOpen(true);
      SweetAlert.fire({
        title: "Success!",
        text: "Payment recorded successfully.",
        icon: "success",
      });
    } catch (error) {
      console.error("Error submitting payment:", error.response?.data || error);
      SweetAlert.fire({
        title: "Error!",
        text: error.response?.data?.detail || "Something went wrong!",
        icon: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
  };

  const printReceipt = () => {
    if (receiptData?.receipt_number) {
      const barcodeElement = document.getElementById("barcode-student");
      const barcodeElement2 = document.getElementById("barcode-admin");
      jsBarcode(barcodeElement, receiptData.receipt_number, { format: "CODE128" });
      jsBarcode(barcodeElement2, receiptData.receipt_number, { format: "CODE128" });

      const element = receiptRef.current;
      const opt = {
        margin: 0,
        filename: `receipt_${receiptData.receipt_number}.pdf`,
        image: { type: "jpeg", quality: 0.98 },
        html2canvas: { scale: 2 },
        jsPDF: { unit: "mm", format: "a4", orientation: "portrait" },
      };
      html2pdf().set(opt).from(element).save();
    } else {
      SweetAlert.fire({
        title: "Error!",
        text: "No receipt data available!",
        icon: "error",
      });
    }
  };

  return (
    <div>
      <Box padding={8}>
        {/* ---- Payment Form ---- */}
        <Box
          component="form"
          onSubmit={handleSubmit}
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 2,
            maxWidth: "800px",
            margin: "auto",
            padding: 2,
          }}
        >
          {/* Student Autocomplete and Class */}
          <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
            <FormControl fullWidth required sx={{ flex: 1 }}>
              <Autocomplete
                disablePortal
                options={students}
                getOptionLabel={(option) => option.full_name || ""}
                value={selectedStudent}
                onChange={(event, newValue) => {
                  setSelectedStudent(newValue);
                }}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                renderInput={(params) => <TextField {...params} label="Select Student" required />}
                ListboxProps={ListboxProps}
              />
            </FormControl>
            <TextField
              label="Student Class"
              value={studentClass}
              onChange={(e) => setStudentClass(e.target.value)}
              fullWidth
              required
              sx={{ flex: 1 }}
            />
          </Box>

          {/* Payer Name and Contact */}
          <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
            <TextField
              label="Payer Name"
              value={payerName}
              onChange={(e) => setPayerName(e.target.value)}
              fullWidth
              required
              sx={{ flex: 1 }}
            />
            <TextField
              label="Payer Contact"
              value={payerContact}
              onChange={(e) => setPayerContact(e.target.value)}
              fullWidth
              required
              sx={{ flex: 1 }}
            />
          </Box>

          {/* Amount Due and Amount Paid */}
          <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
            <TextField
              label="Amount Due"
              type="number"
              value={amountDue}
              onChange={(e) => setAmountDue(e.target.value)}
              fullWidth
              required
              sx={{ flex: 1 }}
            />
            <TextField
              label="Amount Paid"
              type="number"
              value={amountPaid}
              onChange={(e) => setAmountPaid(e.target.value)}
              fullWidth
              required
              sx={{ flex: 1 }}
            />
          </Box>

          {/* Semester and Service */}
          <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
            <FormControl fullWidth required sx={{ flex: 1 }}>
              <InputLabel>Semester</InputLabel>
              <Select
                label="Semester"
                value={semester}
                onChange={(e) => setSemester(e.target.value)}
              >
                <MenuItem value="1st Semester">First Semester</MenuItem>
                <MenuItem value="2nd Semester">Second Semester</MenuItem>
              </Select>
            </FormControl>
            <FormControl fullWidth required sx={{ flex: 1 }}>
              <InputLabel>Service</InputLabel>
              <Select
                label="Service"
                value={services}
                onChange={(e) => setServices(e.target.value)}
              >
                <MenuItem value="School Fee">School Fee</MenuItem>
                <MenuItem value="Wassce Fee">Wassce Fee</MenuItem>
                <MenuItem value="Activities Fee">Activities Fee</MenuItem>
                <MenuItem value="Other Fee">Other Fee</MenuItem>
              </Select>
            </FormControl>
          </Box>

          {/* Payment Date */}
          <TextField
            label="Payment Date"
            type="date"
            value={dateName}
            onChange={(e) => setDateName(e.target.value)}
            fullWidth
            required
            InputLabelProps={{ shrink: true }}
          />

          <Button
            type="submit"
            variant="contained"
            color="primary"
            fullWidth
            disabled={loading}
          >
            {loading ? <CircularProgress size={24} /> : "Submit Payment"}
          </Button>
        </Box>

        {/* ---- Receipt Dialog ---- */}
        <Dialog open={dialogOpen} onClose={handleDialogClose} fullWidth maxWidth="md">
          <DialogContent>
           <div ref={receiptRef} className="receipt-a4">
  {/* ==== STUDENT COPY ==== */}
  <div className="receipt-box student-copy">
    <div className="receipt-header">
      <img src={SchoolLogo} alt="School Logo" className="school-logo" />
   <div className="school-info">
        <h2>Mesiah Mission Institute</h2>
        <p>Morris's Farm Monrovia</p>
        <p>Contact: +231-777-272-609 / +231-770-123-025 / +231-888-886-901</p>
      </div>
    </div>
    <h3>STUDENT COPY</h3>
    <div className="receipt-row">
      <span><strong>Name:</strong> {receiptData?.student_full_name}</span>
      <span><strong>Class:</strong> {receiptData?.student_class}</span>
    </div>
    <div className="receipt-row">
      <span><strong>Receipt ID:</strong> {receiptData?.receipt_number}</span>
      <span><strong>Service:</strong> {receiptData?.services}</span>
    </div>
    <div className="receipt-row">
      <span><strong>Payer:</strong> {receiptData?.payer_name}</span>
      <span><strong>Contact:</strong> {receiptData?.payer_contact}</span>
    </div>
    <div className="receipt-row">
      <span style={{ backgroundColor: "black", color: "white", padding: "4px 8px", borderRadius: "4px" }}><strong>Amount Due:</strong> {receiptData?.amount_due} $</span>
      <span style={{ backgroundColor: "black", color: "white", padding: "4px 8px", borderRadius: "4px" }}><strong >Amount Paid:</strong> {receiptData?.amount_paid} $</span>
      <span style={{ backgroundColor: "black", color: "white", padding: "4px 8px", borderRadius: "4px" }} ><strong>Balance:</strong> {receiptData?.balance} $</span>
    </div>
    <p style={{ textAlign: "center" }}><strong>Date:</strong> {receiptData?.date}</p>
    <div className="barcode-row">
      <svg id="barcode-student" className="barcode"></svg>
    </div>
    <div className="signature-row">
      <div>
        <p>________________________</p>
        <p>Administration</p>
      </div>
      <div>
        <p>________________________</p>
        <p>Business Manager</p>
      </div>
    </div>
  </div>

  {/* Divider (tear line) */}
  <div className="receipt-divider"></div>

  {/* ==== ADMIN COPY ==== */}
  <div className="receipt-box admin-copy">
    <div className="receipt-header">
      <img src={SchoolLogo} alt="School Logo" className="school-logo" />
      <div className="school-info">
        <h2>Mesiah Mission Institute</h2>
        <p>Morris's Farm, Monrovia</p>
        <p>Contact: +231-777-272-609 / +231-770-123-025 / +231-888-886-901</p>
      </div>
    </div>
    <h3>ADMINISTRATION COPY</h3>
    <div className="receipt-row">
      <span><strong>Name:</strong> {receiptData?.student_full_name}</span>
      <span><strong>Class:</strong> {receiptData?.student_class}</span>
    </div>
    <div className="receipt-row">
      <span><strong>Receipt ID:</strong> {receiptData?.receipt_number}</span>
      <span><strong>Service:</strong> {receiptData?.services}</span>
    </div>
    <div className="receipt-row">
      <span><strong>Payer:</strong> {receiptData?.payer_name}</span>
      <span><strong>Contact:</strong> {receiptData?.payer_contact}</span>
    </div>
    <div className="receipt-row">
      <span style={{ backgroundColor: "black", color: "white", padding: "4px 8px", borderRadius: "4px" }}><strong>Amount Due:</strong> {receiptData?.amount_due} $</span>
      <span style={{ backgroundColor: "black", color: "white", padding: "4px 8px", borderRadius: "4px" }}><strong>Amount Paid:</strong> {receiptData?.amount_paid} $</span>
      <span style={{ backgroundColor: "black", color: "white", padding: "4px 8px", borderRadius: "4px" }}><strong>Balance:</strong> {receiptData?.balance} $</span>
    </div>
    <p style={{ textAlign: "center" }}><strong>Date:</strong> {receiptData?.date}</p>
    <div className="barcode-row">
      <svg id="barcode-admin" className="barcode"></svg>
    </div>
    <div className="signature-row">
      <div>
        <p>________________________</p>
        <p>Administration</p>
      </div>
      <div>
        <p>________________________</p>
        <p>Business Manager</p>
      </div>
    </div>
  </div>
</div>

          </DialogContent>
          <DialogActions>
            <Button onClick={printReceipt} variant="contained" color="primary">Export PDF</Button>
            <Button onClick={handleDialogClose} variant="outlined" color="secondary">Close</Button>
          </DialogActions>
        </Dialog>
      </Box>
    </div>
  );
}

export default ReceiptPayment;
